package com.heelion.farmafreelance.ui

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.heelion.farmafreelance.models.Farmacista
import com.heelion.farmafreelance.models.Infermiere
import com.heelion.farmafreelance.BASE_URL
import com.heelion.farmafreelance.agent.CalcolatoreGuadagni
import com.heelion.farmafreelance.agent.RiepilogoGuadagni
import com.heelion.farmafreelance.utils.StimaFiscale
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.math.round

/**
 * "I miei guadagni" del professionista, mese per mese: lordo (ore x tariffa oraria), contributo
 * della cassa stimato (ENPAF per i farmacisti, ENPAPI per gli infermieri) con la percentuale del
 * profilo, netto dopo il contributo e, col regime forfettario, l'imposta sostitutiva
 * stimata ([StimaFiscale]). Stessi calcoli di Farmy ("quanto ho guadagnato a ottobre?") e delle fatture.
 * In alto l'export dell'anno per il commercialista (CSV per Excel o PDF), generato dal server.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun GuadagniScreen(viewModel: AuthViewModel, onBack: () -> Unit) {
    var mesi by remember { mutableStateOf<List<RiepilogoGuadagni>?>(null) }
    var errore by remember { mutableStateOf(false) }
    val farmacista = viewModel.currentUserProfessionista
    // Cassa e percentuale dal profilo: ENPAF per i farmacisti, ENPAPI per gli infermieri.
    val (cassa, percEnpaf) = when (farmacista) {
        is Infermiere -> "ENPAPI" to farmacista.percEnpapi
        is Farmacista -> "ENPAF" to farmacista.percEnpaf
        else -> "ENPAF" to 2.0
    }
    val aliquota = farmacista?.let { StimaFiscale.aliquota(it.regimeFiscale, it.aliquotaForfettario) }
    val oggi = remember { Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date }
    val uriHandler = LocalUriHandler.current

    LaunchedEffect(viewModel.currentMatches, viewModel.confirmedShiftsDetails) {
        val storico = viewModel.getStoricoOppureNull()
        if (storico == null) {
            errore = true
            return@LaunchedEffect
        }
        errore = false
        val dettagli = viewModel.confirmedShiftsDetails.mapNotNull { a -> a.id?.let { it to a } }.toMap()
        mesi = CalcolatoreGuadagni.perMese(CalcolatoreGuadagni.voci(storico, viewModel.currentMatches, dettagli, oggi), percEnpaf, aliquota)
    }

    Scaffold(
        containerColor = Color.Transparent,
        topBar = {
            TopAppBar(
                colors = TopAppBarDefaults.topAppBarColors(containerColor = Color.Transparent),
                title = { Text("I miei guadagni", fontWeight = FontWeight.Bold) },
                navigationIcon = {
                    IconButton(onClick = onBack) { Icon(AppIcons.ArrowBack, contentDescription = "Indietro") }
                }
            )
        }
    ) { padding ->
        Column(modifier = Modifier.fillMaxSize().padding(padding).padding(horizontal = 16.dp)) {
            when {
                errore -> Text(
                    "Non riesco a caricare lo storico dei turni: controlla la connessione e riprova.",
                    color = MaterialTheme.colorScheme.error
                )
                mesi == null -> Box(Modifier.fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) { CircularProgressIndicator() }
                mesi!!.isEmpty() -> Text("Non hai ancora turni svolti o confermati.")
                else -> {
                    Text(
                        "Lordo = ore × tariffa oraria. $cassa stimato al ${formattaNumero(percEnpaf)}% (dal tuo profilo). " +
                            (if (aliquota != null) "Imposta stimata col forfettario: (lordo × 78% − $cassa) × ${formattaNumero(aliquota)}%. " +
                                "È una stima: il calcolo vero lo fa il commercialista. "
                            else "Imposte non stimate: con il regime ordinario dipendono dagli altri redditi. ") +
                            "\"Previsto\" = turni confermati ancora da svolgere.",
                        style = MaterialTheme.typography.bodySmall,
                        color = Color.Gray
                    )
                    Spacer(Modifier.height(12.dp))
                    val farmacistaId = farmacista?.id
                    if (farmacistaId != null) {
                        ExportCommercialista(anni = mesi!!.map { it.anno }.distinct()) { anno, formato ->
                            uriHandler.openUri("$BASE_URL/api/guadagni/export?utenteId=$farmacistaId&role=${viewModel.currentUserRole?.name}&anno=$anno&formato=$formato${com.heelion.farmafreelance.api.FarmaFreelanceApi.parametroToken()}")
                        }
                        Spacer(Modifier.height(12.dp))
                    }
                    LazyColumn(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                        items(mesi!!) { r ->
                            SchedaMese(r, corrente = r.anno == oggi.year && r.mese == oggi.monthNumber, cassa = cassa)
                        }
                        item { Spacer(Modifier.height(24.dp)) }
                    }
                }
            }
        }
    }
}

@Composable
private fun SchedaMese(r: RiepilogoGuadagni, corrente: Boolean, cassa: String) {
    Card(
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(
            containerColor = if (corrente) MaterialTheme.colorScheme.primary.copy(alpha = 0.08f) else Color.White
        ),
        elevation = CardDefaults.cardElevation(defaultElevation = if (corrente) 4.dp else 1.dp),
        modifier = Modifier.fillMaxWidth()
    ) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Text(
                    "${MESI[(r.mese ?: 1) - 1].replaceFirstChar { it.uppercase() }} ${r.anno}" + if (corrente) " · questo mese" else "",
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.weight(1f)
                )
                Text("${r.turni} turni · ${formattaNumero(r.ore)} ore", style = MaterialTheme.typography.bodySmall, color = Color.Gray)
            }
            Riga("Lordo", euro(r.lordo), grassetto = true)
            if (r.lordoPrevisto > 0) Riga("di cui previsto", euro(r.lordoPrevisto))
            Riga("$cassa stimato", "− ${euro(r.enpaf)}")
            HorizontalDivider()
            Riga("Netto dopo $cassa", euro(r.netto), grassetto = r.imposta == null)
            r.imposta?.let { imposta ->
                Riga("Imposta stimata (${formattaNumero(r.aliquota ?: 0.0)}%)", "− ${euro(imposta)}")
                Riga("Netto stimato", euro(r.nettoDopoImposte ?: r.netto), grassetto = true)
            }
        }
    }
}

/** Scarica l'anno scelto per il commercialista: CSV (si apre con Excel) o PDF. */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun ExportCommercialista(anni: List<Int>, onScarica: (anno: Int, formato: String) -> Unit) {
    var anno by remember(anni) { mutableStateOf(anni.max()) }
    Card(shape = RoundedCornerShape(12.dp), colors = CardDefaults.cardColors(containerColor = Color.White), modifier = Modifier.fillMaxWidth()) {
        Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Text("Per il commercialista", style = MaterialTheme.typography.titleSmall, fontWeight = FontWeight.Bold)
            if (anni.size > 1) {
                Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                    anni.sortedDescending().forEach { a ->
                        FilterChip(selected = a == anno, onClick = { anno = a }, label = { Text(a.toString()) })
                    }
                }
            }
            Text("Turni svolti nel $anno con i totali di ogni mese.", style = MaterialTheme.typography.bodySmall, color = Color.Gray)
            Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
                OutlinedButton(onClick = { onScarica(anno, "csv") }) { Text("Scarica Excel (CSV)") }
                OutlinedButton(onClick = { onScarica(anno, "pdf") }) { Text("Scarica PDF") }
            }
        }
    }
}

@Composable
private fun Riga(etichetta: String, valore: String, grassetto: Boolean = false) {
    Row {
        Text(etichetta, modifier = Modifier.weight(1f), style = MaterialTheme.typography.bodyMedium)
        Text(valore, style = MaterialTheme.typography.bodyMedium, fontWeight = if (grassetto) FontWeight.Bold else FontWeight.Normal)
    }
}

private val MESI = listOf("gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio",
    "agosto", "settembre", "ottobre", "novembre", "dicembre")

private fun formattaNumero(v: Double): String =
    if (v % 1.0 == 0.0) v.toLong().toString() else (round(v * 100) / 100).toString().replace('.', ',')

/** "1.470,50 €" */
private fun euro(v: Double): String {
    val centesimi = round(v * 100).toLong()
    val interi = (centesimi / 100).toString().reversed().chunked(3).joinToString(".").reversed()
    return "$interi,${(centesimi % 100).toString().padStart(2, '0')} €"
}
